import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses, sortObjectToList } from 'utils/utils';

import Button from 'components/button/button';
import MovieInfoEditor from 'components/editor/movies/movieInfo/movieInfo';
import FieldWrapper from 'components/editor/settings/fieldWrapper';
import SimpleField from 'components/editor/settings/simpleField';
import { Handle, Sortable, SortableItem } from 'components/sortable/sortable';

import s from 'components/editor/movies/movieInfo/movieInfoUniversalBase.sss';

export default class UniversalBaseMovieInfoEditor extends MovieInfoEditor {
	s = s;

	constructor(props) {
		super(props);

		this.addCredit = this.addCredit.bind(this);
		this.addSection = this.addSection.bind(this);
		this.deleteCredit = this.deleteCreditEntry.bind(this, false);
		this.deleteSection = this.deleteCreditEntry.bind(this, true);
		this.onCreditsReorder = this.onCreditEntryReorder.bind(this, false);
		this.onSectionsReorder = this.onCreditEntryReorder.bind(this, true);

		this.ensureDefaultMovieCredits();
	}

	ensureDefaultMovieCredits() {
		const credits = this.movie?.pages?.synopsis?.credits;
		if (!credits) {
			jsonPointer.setDeep(Store.get().data, `${this.basePath}/content/pages/synopsis/credits`, {
				cast: {
					label: 'STARRING',
					order: 1,
					credits: {
						[crypto.randomUUID()]: { order: 1 },
					},
				},
				directors: {
					label: 'DIRECTED BY',
					order: 2,
					credits: {
						[crypto.randomUUID()]: { order: 1 },
					},
				},
				producers: {
					label: 'PRODUCED BY',
					order: 3,
					credits: {
						[crypto.randomUUID()]: { order: 1 },
					},
				},
				writers: {
					label: 'WRITTEN BY',
					order: 4,
					credits: {
						[crypto.randomUUID()]: { order: 1 },
					},
				},
			});
		}
	}

	addCredit(sectionId) {
		const id = crypto.randomUUID();
		const order = Object.keys(this.movie?.pages?.synopsis?.credits?.[sectionId]?.credits || {}).length + 1;

		jsonPointer.setDeep(Store.get().data, `${this.basePath}/content/pages/synopsis/credits/${sectionId}/credits/${id}`, {
			order: order,
		});
	}

	addSection() {
		const id = crypto.randomUUID();
		const order = Object.keys(this.movie?.pages?.synopsis?.credits || {}).length + 1;

		jsonPointer.setDeep(Store.get().data, `${this.basePath}/content/pages/synopsis/credits/${id}`, {
			order: order,
			credits: {
				[crypto.randomUUID()]: { order: 1 },
			},
		});
	}

	async deleteCreditEntry(isSection, path) {
		const confirmMessage = isSection ? 'Are you sure you want to delete this credit section and all its contents?' : 'Are you sure you want to delete this credit entry?';
		const confirmTitle = isSection ? 'Delete section?' : 'Delete credit?';
		const ok = await confirm(confirmMessage, { title: confirmTitle, button: 'Delete', danger: true });
		if (!ok) return;

		// Soft delete so locales overriding this entry drop it too
		jsonPointer.setDeep(Store.get().data, path, null);
	}

	onCreditEntryReorder(isSection, e) {
		const allItems = [...e.target.querySelectorAll(isSection ? '[data-section-path]' : '[data-credit-path]')].map((el) =>
			isSection ? el.dataset.sectionPath : el.dataset.creditPath
		);
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		allItems.forEach((path, index) => {
			jsonPointer.setDeep(Store.get().data, `${path}/order`, index + 1);
		});
	}

	renderCredit(credit, sectionPath) {
		const readonly = !canUser('write');
		const path = `${sectionPath}/credits/${credit.id}`; 

		return (
			<SortableItem key={credit.id} class={s.credit} data-credit-path={path}>
				<Handle />
				<SimpleField showOnHover class={s.nameField} path={`${path}/name`} placeholder="Add credit..." disabled={readonly} />
				<Button icon="cancel" disabled={readonly} onClick={() => this.deleteCredit(path)} danger text />
				<div class={s.addCredit} /> {/* Add empty div to account for the width of the add sub credit button */}
			</SortableItem>
		);
	}

	renderSectionHeader(section, sectionPath = `${this.basePath}/content/pages/synopsis/credits/${section.id}`) {
		const readonly = !canUser('write');

		return (
			<div class={s.sectionHeader}>
				<Handle />
				<SimpleField showOnHover class={joinClasses(s.nameField, s.headingField)} path={`${sectionPath}/label`} placeholder="Add section..." disabled={readonly} />
				<Button icon="cancel" disabled={readonly} onClick={() => this.deleteSection(sectionPath)} danger text />
				<Button icon="plus" class={s.addCredit} disabled={readonly} onClick={() => this.addCredit(section.id)}>Add credit</Button>
			</div>
		);
	}

	renderSection(section) {
		const readonly = !canUser('write');
		const path = `${this.basePath}/content/pages/synopsis/credits/${section.id}`;
		const credits = sortObjectToList(section.credits);

		return (
			<SortableItem key={section.id} class={s.section} data-section-path={path}>
				{this.renderSectionHeader(section, path)}
				<Sortable class={s.sectionContent} disabled={readonly} onChange={this.onCreditsReorder}>
					{credits.map(credit => this.renderCredit(credit, path))}
				</Sortable>
			</SortableItem>
		);
	}

	renderMovieCredits() {
		const readonly = !canUser('write');
		const sections = sortObjectToList(this.movie?.pages?.synopsis?.credits);

		return (
			<FieldWrapper label="Credits">
				<Sortable class={s.movieCredits} disabled={readonly} onChange={this.onSectionsReorder}>
					{sections.map(section => this.renderSection(section))}
					<Button icon="plus" disabled={readonly} onClick={this.addSection}>
						Add section
					</Button>
				</Sortable>
			</FieldWrapper>
		);
	}

	renderMagicGenre() {
		return super.renderMagicGenre({
			inheritPath: null,
			defaultValue: '',
			disabled: false,
		});
	}

	renderEssenceGenre() {
		return super.renderEssenceGenre({
			inheritPath: null,
			defaultValue: '',
			disabled: false,
		});
	}

	renderIMDBId() {
		return super.renderIMDBId({
			inheritPath: null,
			defaultValue: '',
			disabled: false,
		});
	}

	renderFields() {
		return [
			this.renderTitle(),
			this.renderShowtimesSource(),
			this.renderMovieCredits(),
		];
	}

	renderDescription() {
		return [
			'Edit information and media related to this movie.',
			'Updates will be reflected across all locales by default.',
			'Items with local overrides (such as localised copy) will not be affected.',
		].filter(Boolean).map(str => <p>{str}</p>);
	}

	renderDataLayerSettings() {
		return (
			<div class={s.section}>
				{this.renderDataLayerSectionHeader()}
				<div class={s.fields}>
					{this.renderDataLayerFields()}
				</div>
			</div>
		);
	}
}